import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormLabel, HStack, Icon, Input, useDisclosure, Text, useToast, Checkbox } from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import DatePicker from "react-datepicker";
import { FaEye } from "react-icons/fa";

import ArrowIcon from "@/assets/ArrowIcon";
import { COLORS, DURATION_TOAST, FORMAT_ADD_DATE, FORMAT_UI_DATE } from "@/common/constants";
import EditFile from "@/components/Fields/EditFile";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import SelectAssignReviewer from "@/components/Selects/SelectAssignReviewer";
import { RootState } from "@/redux/store";
import { useAppSelector } from "@/redux/hooks";
import { getListReviewer, getSireReviewById, reassignSireReview, SireReview } from "@/services/sireReviewService";
import { downloadFile } from "@/services/downloadService";
import { Option } from "@/utils/select";
import { format } from "date-fns";

const ReassignSireReviewAdmin = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);
    const location = useLocation();

    const { id } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [listReviewer, setListReviewer] = useState<any[]>([]);
    const [dataDetail, setDataDetail] = useState<SireReview | null>(null);

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to reassign SIRE review?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = async (data: any) => {
        setTitle("Reassign SIRE Review?");
        setConfirmationTest("Are you sure you want to reassign?");
        setConfirmationTitleButton("Reassign SIRE Review");
        onOpen();
        setDataAdd(data);
        setIsSubmitting(true);
    }

    const generateData = () => {
        const data = new FormData();
        data.append('SireReviewId', id);
        data.append('Inspectorid', dataAdd.assignReviewer || '');

        if (Array.isArray(dataAdd.OCIMFSireReportFile)) {
            dataAdd.OCIMFSireReportFile.forEach((file: File) => {
                data.append('OCIMFSireReportFile', file);
            });
        } else if (dataAdd.OCIMFSireReportFile) {
            data.append('OCIMFSireReportFile', dataAdd.OCIMFSireReportFile);
        } else {
            data.append('OCIMFSireReportFile', '');
        }


        if (Array.isArray(dataAdd.OfficerMatrixFile)) {
            dataAdd.OfficerMatrixFile.forEach((file: File) => {
                data.append('OfficerMatrixFile', file);
            });
        } else if (dataAdd.OfficerMatrixFile) {
            data.append('OfficerMatrixFile', dataAdd.OfficerMatrixFile);
        } else {
            data.append('OfficerMatrixFile', '');
        }
        data.append('OCIMFSireReportDateTime', dataAdd.OCIMFSireReportDate ? format(dataAdd.OCIMFSireReportDate, FORMAT_ADD_DATE) : '');
        data.append('OfficerMatrixDateTime', dataAdd.OfficerMatrixDate ? format(dataAdd.OfficerMatrixDate, FORMAT_ADD_DATE) : '');
        data.append('ModifiedBy', name || '');
        data.append('IsRerequest', dataAdd.isRerequest || false);
        data.append('IsSecondDownload', dataAdd.isSecondDownload || false);
        data.append('Notes', dataAdd.notes || '');
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to reassign SIRE review?") {
            navigate('/manage-sire-review');
            return;
        }

        const obj = generateData();
        const response = await reassignSireReview(id, obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'SIRE review reassigned.',
                description: "We've reassigned the SIRE review for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/manage-sire-review')
            }, 1000);
        } else {
            toast({
                title: 'SIRE review failed to reassign.',
                description: "We unable to reassign SIRE review for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleDrop = (
        acceptedFiles: File[],
        setFieldValue: (fieldName: string, value: File[] | File) => void,
        fieldName: string
    ) => {
        setFieldValue(fieldName, acceptedFiles);
    };

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const getListAssignReviewer = async () => {
        const response = await getListReviewer() as any;
        if (response?.status === HttpStatusCode.Ok) {
            const formattedData = response?.data?.map((vessel: any) => ({
                value: vessel.inspectorId,
                label: vessel.inspectorName,
            })) || [];

            setListReviewer(formattedData);
        }
    };
    
    const getDetailSire = async () => {
        const response = await getSireReviewById(id) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);
        }
    }

    useEffect(() => {
        getListAssignReviewer();
    }, []);

    useEffect(() => {
        getDetailSire();
    }, [id]);

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        assignReviewer: dataDetail?.inspectorid ?? '',
                        requestor: dataDetail?.requestor ?? '',
                        pic: dataDetail?.pic ?? '',
                        shipName: dataDetail?.vesselName ?? '',
                        imoNumber: dataDetail?.imoNumber ?? '',
                        vesselType: dataDetail?.vesselType ?? '',
                        requestDate: dataDetail?.reviewDate ?? '',
                        addAld: dataDetail?.addaldPertamina ?? '',
                        classSurveyStatus: dataDetail?.classSurveyStatusId ?? '',
                        capSurveyReport: dataDetail?.capSurveyReportId ?? '',
                        certificates: dataDetail?.allStatuoryCertificatesId ?? '',
                        other: dataDetail?.othersId ?? '',
                        OCIMFSireReportFile: '',
                        OCIMFSireReportDate: dataDetail?.ocimfSireReportDate ?? null,
                        OfficerMatrixFile: '',
                        OfficerMatrixDate: dataDetail?.officerMatrixDate ?? null,
                        dwt: dataDetail?.dwt ?? '',
                        shipBuildDate: dataDetail?.shipBuildDate ?? '',
                        notes: dataDetail?.notes || '',
                        isRerequest: dataDetail?.isRerequest || false,
                        isSecondDownload: dataDetail?.isSecondDownload || false,

                    }}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name='assignReviewer'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.assignReviewer && form.touched.assignReviewer)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Assign Reviewer</FormLabel>
                                            <div className="form-input-container">
                                                <SelectAssignReviewer
                                                    placeholder="Choose Reviewer"
                                                    name='assignReviewer'
                                                    handleChange={({ value }: Option) => props.setFieldValue('assignReviewer', value)}
                                                    value={props.values.assignReviewer}
                                                    options={listReviewer}
                                                    hasError={form.errors.assignReviewer && form.touched.assignReviewer}
                                                    isDisabled={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.assignReviewer)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='requestor'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.requestor && form.touched.requestor)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Requestor<br /><em style={{ color: '#344054', fontSize: '12px' }}>(Company Name)</em></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Requestor' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.requestor)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='pic'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.pic && form.touched.pic)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Person in Charge (PIC)</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input PIC' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.pic)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='shipName'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.shipName && form.touched.shipName)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Name</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Vessel Name' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.shipName)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='imoNumber'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.imoNumber && form.touched.imoNumber)} marginBottom={4}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor="type" className="form-label">IMO Number</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input IMO Number' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.imoNumber)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='vesselType'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.vesselType && form.touched.vesselType)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Vessel Type</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input Vessel Type' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.vesselType)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='requestDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.requestDate && form.touched.requestDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Request Date</FormLabel>
                                            <div className={form.errors.requestDate && form.touched.requestDate ? "form-date-picker form-date-picker-error" : "form-date-picker"}>
                                                <DatePicker
                                                    id="requestDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('requestDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Invoice Date"
                                                    readOnly={isSubmitting}
                                                    disabled
                                                />
                                                <FormErrorMessage className="form-date-picker-error-message">{String(form.errors.requestDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='addAld'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.addAld)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">ADD / ALD Pertamina</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Input ADD ALD' className="form-input" disabled={!!dataDetail?.addaldPertamina} />
                                                <FormErrorMessage>{String(form.errors.addAld)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {dataDetail?.classSurveyStatusId &&
                                <Field name='classSurveyStatus'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.classSurveyStatus && form.touched.classSurveyStatus)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <div className="form-label form-label-with-info">
                                                    <FormLabel>Class Survey Status</FormLabel>
                                                    <span>
                                                        {(dataDetail.classSurveyStatusDate && !isNaN(new Date(dataDetail.classSurveyStatusDate).getTime()))
                                                            ? format(new Date(dataDetail.classSurveyStatusDate), FORMAT_UI_DATE)
                                                            : ''}
                                                    </span>
                                                </div>
                                                <div className="form-input-container">
                                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Box
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={4}
                                                            backgroundColor={dataDetail?.classSurveyStatusId ? "green.50" : "yellow.50"}
                                                            borderColor={dataDetail?.classSurveyStatusId ? "green.200" : "yellow.200"}
                                                            width="100%"
                                                            marginTop="15px"
                                                        >
                                                            <HStack
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={3}
                                                                backgroundColor="white"
                                                                justifyContent="space-between"
                                                                width="100%"
                                                            >
                                                                <HStack>
                                                                    <Text>{dataDetail?.classSurveyStatusId ? 'Class Survey Status File' : 'No file uploaded'}</Text>
                                                                </HStack>
                                                                <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.classSurveyStatusId)}>
                                                                    {dataDetail?.classSurveyStatusId && <Icon as={FaEye} />}
                                                                    <Text>{dataDetail?.classSurveyStatusId ? 'Download Document' : ''}</Text>
                                                                </HStack>
                                                            </HStack>
                                                        </Box>
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.classSurveyStatus)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            {dataDetail?.capSurveyReportId &&
                                <Field name='capSurveyReport'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.capSurveyReport && form.touched.capSurveyReport)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <div className="form-label form-label-with-info">
                                                    <FormLabel>CAP/CAS Survey Report</FormLabel>
                                                    <span>
                                                        {(dataDetail.capSurveyReportDate && !isNaN(new Date(dataDetail.capSurveyReportDate).getTime()))
                                                            ? format(new Date(dataDetail.capSurveyReportDate), FORMAT_UI_DATE)
                                                            : ''}
                                                    </span>
                                                </div>
                                                <div className="form-input-container">
                                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Box
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={4}
                                                            backgroundColor={dataDetail?.capSurveyReportId ? "green.50" : "yellow.50"}
                                                            borderColor={dataDetail?.capSurveyReportId ? "green.200" : "yellow.200"}
                                                            width="100%"
                                                            marginTop="15px"
                                                        >
                                                            <HStack
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={3}
                                                                backgroundColor="white"
                                                                justifyContent="space-between"
                                                                width="100%"
                                                            >
                                                                <HStack>
                                                                    <Text>{dataDetail?.capSurveyReportId ? 'CAP/CAS Survey Report File' : 'No file uploaded'}</Text>
                                                                </HStack>
                                                                <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.capSurveyReportId)}>
                                                                    {dataDetail?.capSurveyReportId && <Icon as={FaEye} />}
                                                                    <Text>{dataDetail?.capSurveyReportId ? 'Download Document' : ''}</Text>
                                                                </HStack>
                                                            </HStack>
                                                        </Box>
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.capSurveyReport)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            {dataDetail?.allStatuoryCertificatesId &&
                                <Field name='certificates'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.certificates && form.touched.certificates)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <div className="form-label form-label-with-info">
                                                    <FormLabel>All Statutory Certificates</FormLabel>
                                                    <span>
                                                        {(dataDetail.allStatuoryCertificatesDate && !isNaN(new Date(dataDetail.allStatuoryCertificatesDate).getTime()))
                                                            ? format(new Date(dataDetail.allStatuoryCertificatesDate), FORMAT_UI_DATE)
                                                            : ''}
                                                    </span>
                                                </div>
                                                <div className="form-input-container">
                                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Box
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={4}
                                                            backgroundColor={dataDetail?.allStatuoryCertificatesId ? "green.50" : "yellow.50"}
                                                            borderColor={dataDetail?.allStatuoryCertificatesId ? "green.200" : "yellow.200"}
                                                            width="100%"
                                                            marginTop="15px"
                                                        >
                                                            <HStack
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={3}
                                                                backgroundColor="white"
                                                                justifyContent="space-between"
                                                                width="100%"
                                                            >
                                                                <HStack>
                                                                    <Text>{dataDetail?.allStatuoryCertificatesId ? 'All Statutory Certificates File' : 'No file uploaded'}</Text>
                                                                </HStack>
                                                                <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.allStatuoryCertificatesId)}>
                                                                    {dataDetail?.allStatuoryCertificatesId && <Icon as={FaEye} />}
                                                                    <Text>{dataDetail?.allStatuoryCertificatesId ? 'Download Document' : ''}</Text>
                                                                </HStack>
                                                            </HStack>
                                                        </Box>
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.certificates)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }
                            {dataDetail?.othersId &&
                                <Field name='other'>
                                    {({ form }: FieldProps) => (
                                        <FormControl isInvalid={Boolean(form.errors.other && form.touched.other)} style={{ marginBottom: 15 }}>
                                            <div className="form-field-container">
                                                <div className="form-label form-label-with-info">
                                                    <FormLabel>Others</FormLabel>
                                                    {(dataDetail.othersDate && !isNaN(new Date(dataDetail.othersDate).getTime())) ? (
                                                        <span>{format(new Date(dataDetail.othersDate), FORMAT_UI_DATE)}</span>
                                                    ) : null}
                                                </div>
                                                <div className="form-input-container">
                                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        <Box
                                                            borderWidth="1px"
                                                            borderRadius="md"
                                                            padding={4}
                                                            backgroundColor={dataDetail?.othersId ? "green.50" : "yellow.50"}
                                                            borderColor={dataDetail?.othersId ? "green.200" : "yellow.200"}
                                                            width="100%"
                                                            marginTop="15px"
                                                        >
                                                            <HStack
                                                                borderWidth="1px"
                                                                borderRadius="md"
                                                                padding={3}
                                                                backgroundColor="white"
                                                                justifyContent="space-between"
                                                                width="100%"
                                                            >
                                                                <HStack>
                                                                    <Text>{dataDetail?.othersId ? 'Other File' : 'No file uploaded'}</Text>
                                                                </HStack>
                                                                <HStack cursor='pointer' onClick={() => handleDownload(dataDetail?.othersId || '')}>
                                                                    {dataDetail?.othersId && <Icon as={FaEye} />}
                                                                    <Text>{dataDetail?.othersId ? 'Download Document' : ''}</Text>
                                                                </HStack>
                                                            </HStack>
                                                        </Box>
                                                    </div>
                                                    <FormErrorMessage>{String(form.errors.other)}</FormErrorMessage>
                                                </div>
                                            </div>
                                        </FormControl>
                                    )}
                                </Field>
                            }

                            <Field name='OCIMFSireReportFile'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.OCIMFSireReportFile && form.touched.OCIMFSireReportFile)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">
                                                OCIMF Sire Report
                                                <br />
                                                <em style={{ color: '#344054', fontSize: '12px' }}>(max. 6 months)</em>
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    handleDrop={(e) => handleDrop(e, form.setFieldValue, 'OCIMFSireReportFile')}
                                                    isInvalid={Boolean(form.errors.OCIMFSireReportFile && form.touched.OCIMFSireReportFile)}
                                                    isDisabled={form.isSubmitting}
                                                    type="sire-review"
                                                    fileId={typeof(dataDetail?.ocimfSireReportId) === 'string' ? dataDetail?.ocimfSireReportId : undefined}
                                                    isDownloadFolder={false}
                                                    title="OCIMF Sire Report File"
                                                />
                                                <FormErrorMessage>{String(form.errors.OCIMFSireReportFile)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {dataDetail?.isRerequest && <Field name='isRerequest'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Is Re-request {dataDetail?.isRerequest ? 'a' : 'b'}</FormLabel>
                                            <div className="form-input-container"  style={{ flexDirection: 'row' }}>
                                                <Checkbox isChecked={field.value} style={{ marginRight: 30 }} disabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>}
                            <Field name='isSecondDownload'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Is 2nd Downloaded</FormLabel>
                                            <div className="form-input-container" style={{ flexDirection: 'row' }}>
                                                <Checkbox isChecked={field.value} style={{ marginRight: 30 }} onChange={(e) => props.setFieldValue('isSecondDownload', e.target.checked)} />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='OfficerMatrixFile'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.OfficerMatrixFile && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Officer Matrix<br /><em style={{ color: '#344054', fontSize: '12px' }}>(max. 15 days' latest update)</em></FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    handleDrop={(e) => handleDrop(e, form.setFieldValue, 'OfficerMatrixFile')}
                                                    isInvalid={Boolean(form.errors.OfficerMatrixFile && form.touched.OfficerMatrixFile)}
                                                    isDisabled={form.isSubmitting}
                                                    type="sire-review"
                                                    fileId={typeof(dataDetail?.officerMatrixId) === 'string' ? dataDetail?.officerMatrixId : undefined}
                                                    isDownloadFolder={false}
                                                    title="Officer Matrix File"
                                                />
                                                <FormErrorMessage>{String(form.errors.OfficerMatrixFile)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {dataDetail?.notes && <Field name='notes'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.notes)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Notes</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='' className="form-input" disabled />
                                                <FormErrorMessage>{String(form.errors.notes)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            }
                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Reassign SIRE Review
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default ReassignSireReviewAdmin;
